import "../paged-table.scss";
import { LocalDate } from "@js-joda/core";
import { ComplianceDocument, DashboardComplianceItem } from "../../../../models/compliance";
import { assertNever } from "../../../../scripts/consts/assertNever.const";
import { caregiverStatus } from "../../../../scripts/consts/commonConsts";
import { dateUtils } from "../../../../scripts/consts/dateUtils";
import { CaregiverStatus } from "../../../../scripts/messages/caregiver";
import {
  ComplianceDashboardPageRequest,
  ComplianceDashboardSortField,
  ComplianceStatsResponse,
  MessageComplianceDashboardCaregiver,
} from "../../../../scripts/messages/compliance";
import {
  AgencyId,
  CaregiverCertification,
  CaregiverDocumentTypeId,
  CaregiverId,
} from "../../../../scripts/messages/ids";
import { ComplianceParser } from "../../../../parsers/compliance-parser";
import { CompService } from "../../comp.service";
import { getCaregiverStatusColor, LabelColor } from "../../../../scripts/utils/caregiverUtils";
import {
  getCaregiverOverviewComplianceStatusColor,
  getComplianceItemOverviewStatusColor,
  parseSectionLabel,
  sumItemsStatusGroups,
} from "../../compliance.utils";
import { groupBy, upperCaseFirst } from "../../../../scripts/utils/generalUtils";
import "../compliance-dashboard/compliance-dashboard.component.scss";
import { MultiselectCallbackParameter } from "../../../shared/components/multiselect/multiselect.component";
import {
  ChartLabel,
  PieChartColor,
  PieChartEntry,
} from "../../../shared/components/pie-chart/pie-chart.component";

type ChartName = "employmentStatus" | "complianceStatus" | "itemsComplianceStatus";

type CardName =
  | "incompliantCaregiversWithActiveCase"
  | "caregiversWithOnlyOneIncompliantItem"
  | "incompliantItemsWithUploadedDocuments"
  | "pendingApplicationCaregivers";

type CaregiverComplianceStatus = "Compliant" | "Not compliant";
type IsMedflytCG = "Yes" | "No";
type HasHireDate = "Yes" | "No";

/** The filters that narrow by caregiver id rather than by a column value. */
type IdFilterKey = CardName | ChartName;

interface ComplianceItemCell extends DashboardComplianceItem {
  display: {
    text: string;
    color: LabelColor;
  };
}

interface TableRow {
  caregiver: MessageComplianceDashboardCaregiver & {
    complianceStatus: CaregiverComplianceStatus;
    complianceStatusColor: "red" | "green";
  };
  /** Keyed by document type name, which is what the column headers are. */
  items: Record<string, ComplianceItemCell>;
  /**
   * Which followup document types this caregiver is missing. Per row, because
   * the agency-wide map this replaces was half of what kept the old dashboard
   * downloading everything.
   */
  missingFollowupDocumentTypeIds: Set<CaregiverDocumentTypeId>;
  isChecked: boolean;
}

interface TableColumn {
  title: string;
  section: string;
  colored: boolean;
  sort?: "asc" | "desc";
  /** Only the three caregiver columns can be ordered by - see `SORTABLE_COLUMNS`. */
  sortable: boolean;
  show: boolean;
  caregiverDocumentTypeId: CaregiverDocumentTypeId | null;
}

interface TableColumnGroup {
  section: string;
  columns: TableColumn[];
  count: number;
  show: boolean;
}

interface FilterChip {
  key: string;
  title: string;
  values: string[];
}

/**
 * Which column header maps to which server sort field.
 *
 * The document-type columns are absent deliberately: their cells hold compliance
 * statuses, which are not in the search index, so the server cannot order the
 * whole result set by one. Sorting just the loaded page would look like sorting
 * everything, so those headers are inert instead.
 */
const SORTABLE_COLUMNS: Record<string, ComplianceDashboardSortField> = {
  "Caregiver Name": "name",
  ID: "displayId",
  Compliance: "isCompliant",
};

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * The compliance dashboard, one page at a time.
 *
 * Reached while the "Caregiver memory improvements" beta feature is on. Its
 * predecessor - `complianceDashboardComponent`, still serving this tab with the
 * feature off - held every caregiver in the agency as a dictionary and every
 * compliance item in the agency alongside it, joined them in the browser, and
 * then filtered, sorted and paged the result here. This asks the server for the
 * rows on screen.
 *
 * The consequence to keep in mind when changing it: there is no local collection
 * to reach into. A filter, a sort or a column that needs data the page has not
 * been sent has to be added to the endpoint, not read off a map.
 *
 * It also does not broadcast `got_compliance_data`. The sibling Items Status and
 * Caregiver Status tabs are still whole-agency and fetch `/compliance` for
 * themselves, lazily, when they are opened.
 */
//! @ngInject
class ComplianceDashboardPagedCtrl implements ng.IComponentController {
  /* ---------------------------------------------------------------- state -- */

  public rows: TableRow[] = [];
  public totalCount = 0;
  public isTruncated = false;

  public offset = 0;
  public limit = 25;
  public readonly pageSizes = PAGE_SIZES;

  private sortField: ComplianceDashboardSortField = "name";
  private sortOrder: "asc" | "desc" = "asc";

  public isLoadingRows = true;

  /**
   * A refetch with nothing on screen yet - the only time the table is replaced
   * by a shimmer. Every later one keeps the table and dims it, so a page turn
   * or a filter change does not collapse the page and lose the scroll position.
   */
  public isFirstLoadRows = () => this.isLoadingRows && this.rows.length === 0;

  /** A refetch with rows still on screen. */
  public isReloadingRows = () => this.isLoadingRows && this.rows.length > 0;
  public isLoadingStats = true;
  public isOnboardingAgency = false;

  public stats?: ComplianceStatsResponse;
  public charts: Record<ChartName, PieChartEntry[]> = {
    employmentStatus: [],
    complianceStatus: [],
    itemsComplianceStatus: [],
  };

  public documentTypes?: ComplianceDocument[];
  private documentTypesMap: Record<string, ComplianceDocument> = {};
  public dropdownDocumentOptions: { id: CaregiverDocumentTypeId; label: string }[] = [];
  private selectedDocumentTypeIds: Set<CaregiverDocumentTypeId> = new Set();

  public tableDocumentsColumns: TableColumn[] = [];
  public tableDocumentsColumnsGroups: TableColumnGroup[] = [];

  public selectedItems: Set<TableRow> = new Set();

  public filters = {
    freeText: "",
    dropdowns: {
      caregiverStatus: {
        options: caregiverStatus.dropdown,
        selected: [{ id: "ACTIVE", label: "ACTIVE" }],
      },
      complianceStatus: {
        options: ["Compliant", "Not compliant"].map((status) => ({ id: status, label: status })),
        selected: [] as { id: string }[],
      },
      medflytCG: {
        options: ["Yes", "No"].map((status) => ({ id: status, label: status })),
        selected: [] as { id: string }[],
      },
      hasHireDate: {
        options: ["Yes", "No"].map((status) => ({ id: status, label: status })),
        selected: [] as { id: string }[],
      },
      certifications: {
        options: ["HHA", "PCA", "RN"].map((status) => ({ id: status, label: status })),
        selected: [
          { id: "HHA", label: "HHA" },
          { id: "PCA", label: "PCA" },
        ] as { id: string; label?: string }[],
      },
    },
  };

  /**
   * The id-list filters that are currently on.
   *
   * Several can be on at once and the old dashboard ANDed them, so these are
   * intersected. Kept as the raw lists rather than as one set so that turning one
   * off restores the others exactly.
   */
  private idFilters: Map<IdFilterKey, CaregiverId[]> = new Map();

  public readonly idFilterTitles: Record<IdFilterKey, string> = {
    incompliantCaregiversWithActiveCase: "Not compliant caregivers are active on a case",
    caregiversWithOnlyOneIncompliantItem: "Are missing only 1 item to be compliant",
    incompliantItemsWithUploadedDocuments: "Non-compliant items that are available on the passport",
    pendingApplicationCaregivers: "Caregivers in pending application",
    employmentStatus: "Agency employment status",
    complianceStatus: "Agency compliance status",
    itemsComplianceStatus: "Compliance status per item",
  };

  public cardsFromServerData: Partial<Record<CardName, { icon: string; title: string }>> = {
    incompliantCaregiversWithActiveCase: {
      icon: "fa-calendar",
      title: this.idFilterTitles.incompliantCaregiversWithActiveCase,
    },
    caregiversWithOnlyOneIncompliantItem: {
      icon: "fa-bolt",
      title: this.idFilterTitles.caregiversWithOnlyOneIncompliantItem,
    },
    incompliantItemsWithUploadedDocuments: {
      icon: "fa-id-card",
      title: this.idFilterTitles.incompliantItemsWithUploadedDocuments,
    },
  };

  public activeFilters: FilterChip[] = [];

  /** Labels the pie charts are currently hiding, so a redraw keeps them hidden. */
  private hiddenChartLabels: Record<ChartName, string[]> = {
    employmentStatus: [],
    complianceStatus: [],
    itemsComplianceStatus: [],
  };

  constructor(
    private compService: CompService,
    private toaster: toaster.IToasterService,
    private $rootScope: ng.IRootScopeService,
    private $scope: ng.IScope,
    private mfModal: any
  ) {
    this.isOnboardingAgency = this.$rootScope.agencyId === AgencyId.wrap(10001);
  }

  $onInit = () => {
    this.getStats();
    // The page opens on ACTIVE caregivers with HHA and PCA certifications, so
    // those chips have to be there from the start - otherwise the grid is
    // filtered by something the user was never shown.
    this.refreshFilterChips();
    this.loadPage();
  };

  /* ----------------------------------------------------------- page loading -- */

  private buildRequest = (): ComplianceDashboardPageRequest => {
    const search = this.filters.freeText.trim();
    const dropdowns = this.filters.dropdowns;

    return {
      search: search === "" ? null : search,
      statuses: dropdowns.caregiverStatus.selected.map((entry) => entry.id as CaregiverStatus),
      certifications: dropdowns.certifications.selected.map(
        (entry) => entry.id as unknown as CaregiverCertification
      ),
      isCompliant: this.pickTriState(dropdowns.complianceStatus.selected, "Compliant"),
      isFromLandingPage: this.pickTriState(dropdowns.medflytCG.selected, "Yes"),
      hasHireDate: this.pickTriState(dropdowns.hasHireDate.selected, "Yes"),
      caregiverIds: this.effectiveIdFilter(),
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      limit: this.limit,
      offset: this.offset,
    };
  };

  /**
   * A yes/no dropdown as a nullable boolean: selecting both options, or neither,
   * is not a filter at all.
   */
  private pickTriState = (selected: { id: string }[], trueId: string): boolean | null => {
    if (selected.length !== 1) {
      return null;
    }

    return selected[0].id === trueId;
  };

  /**
   * The intersection of every active id filter, or null when none is active.
   *
   * An empty array is a meaningful answer - "these filters agree on nobody" -
   * and the endpoint honours it rather than treating it as no filter.
   */
  private effectiveIdFilter = (): CaregiverId[] | null => {
    const lists = [...this.idFilters.values()];

    if (lists.length === 0) {
      return null;
    }

    return lists.reduce((intersection, list) => {
      const allowed = new Set(list.map((id) => String(id)));

      return intersection.filter((id) => allowed.has(String(id)));
    });
  };

  public loadPage = () => {
    this.isLoadingRows = true;

    this.compService
      .fetchDashboardPage(this.buildRequest())
      .then((data) => {
        const followupDocumentTypesMap = ComplianceParser.getFollowupDocumentTypesMap(
          data.documents
        );
        const documentsMap = ComplianceParser.parseComplianceDocumentMap(
          data.documents,
          followupDocumentTypesMap
        );

        this.setDocumentTypes(
          data.documents.map((document) =>
            ComplianceParser.parseComplianceDocument(document, followupDocumentTypesMap)
          )
        );

        this.rows = data.rows.map((row) => ({
          caregiver: {
            ...row.caregiver,
            complianceStatus: row.caregiver.isCompliant ? "Compliant" : "Not compliant",
            complianceStatusColor: row.caregiver.isCompliant ? "green" : "red",
          },
          items: Object.fromEntries(
            row.items
              .map((item) => ComplianceParser.parseDashboardComplianceItem(item, documentsMap))
              .map((item) => [
                item.documentType.name,
                { ...item, display: this.parseItemDisplayData(item) },
              ])
          ),
          missingFollowupDocumentTypeIds: new Set(row.missingFollowupDocumentTypeIds),
          isChecked: false,
        }));

        this.totalCount = data.totalCount;
        this.isTruncated = data.isTruncated;
        // A page of rows is a different set of objects, so a selection made on
        // the previous page no longer refers to anything on screen.
        this.selectedItems = new Set();
      })
      .catch((err) => {
        console.error(err);
        this.toaster.error("Could not load compliance items");
      })
      .finally(() => {
        this.isLoadingRows = false;
      });
  };

  /** Every filter change is a new result set, so it starts at the first page. */
  private reload = () => {
    this.offset = 0;
    this.refreshFilterChips();
    this.loadPage();
  };

  /* --------------------------------------------------------------- paging -- */

  public get pageNumber(): number {
    return Math.floor(this.offset / this.limit) + 1;
  }

  public get pageCount(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.limit));
  }

  public get rangeStart(): number {
    return this.totalCount === 0 ? 0 : this.offset + 1;
  }

  public get rangeEnd(): number {
    return Math.min(this.offset + this.limit, this.totalCount);
  }

  public canGoPrevious = () => this.offset > 0;

  public canGoNext = () => this.offset + this.limit < this.totalCount;

  public previousPage = () => {
    if (!this.canGoPrevious()) {
      return;
    }

    this.offset = Math.max(0, this.offset - this.limit);
    this.loadPage();
  };

  public nextPage = () => {
    if (!this.canGoNext()) {
      return;
    }

    this.offset = this.offset + this.limit;
    this.loadPage();
  };

  public setPageSize = (size: number) => {
    if (size === this.limit) {
      return;
    }

    this.limit = size;
    this.offset = 0;
    this.loadPage();
  };

  /* -------------------------------------------------------------- sorting -- */

  public onClickSort = (columnTitle: string) => {
    const field = SORTABLE_COLUMNS[columnTitle];

    if (field === undefined) {
      return;
    }

    const order = this.sortField === field && this.sortOrder === "asc" ? "desc" : "asc";

    this.sortField = field;
    this.sortOrder = order;

    this.tableDocumentsColumns = this.tableDocumentsColumns.map((column) => ({
      ...column,
      sort: column.title === columnTitle ? order : undefined,
    }));

    // Ordering changes which rows are on the page, so it is the server's answer
    // that changes, not the order of what is already here.
    this.offset = 0;
    this.loadPage();
  };

  /* -------------------------------------------------------------- filters -- */

  public onSearchChange = () => this.reload();

  public onCaregiverStatusSelect = (selected: MultiselectCallbackParameter<CaregiverStatus>) => {
    this.filters.dropdowns.caregiverStatus.selected = selected.map((entry) => ({
      id: String(entry.id),
      label: String(entry.id),
    }));
    this.reload();
  };

  public onComplianceStatusSelect = (
    selected: MultiselectCallbackParameter<CaregiverComplianceStatus>
  ) => {
    this.filters.dropdowns.complianceStatus.selected = selected.map((entry) => ({
      id: String(entry.id),
    }));
    this.reload();
  };

  public onMedflytCGStatusSelect = (selected: MultiselectCallbackParameter<IsMedflytCG>) => {
    this.filters.dropdowns.medflytCG.selected = selected.map((entry) => ({
      id: String(entry.id),
    }));
    this.reload();
  };

  public onHireDateStatusSelect = (selected: MultiselectCallbackParameter<HasHireDate>) => {
    this.filters.dropdowns.hasHireDate.selected = selected.map((entry) => ({
      id: String(entry.id),
    }));
    this.reload();
  };

  public onCerificationSelect = (
    selected: MultiselectCallbackParameter<CaregiverCertification>
  ) => {
    this.filters.dropdowns.certifications.selected = selected.map((entry) => ({
      id: String(entry.id),
    }));

    // The columns follow the certifications, as they did before, but they are
    // derived in `buildColumns` from this state rather than computed here - the
    // document types may not have arrived yet on a first render, and computing
    // them now would leave the grid with no document columns at all.
    this.buildColumns();
    this.reload();
  };

  public hasIdFilter = (key: IdFilterKey) => this.idFilters.has(key);

  public onCardClick = (card: CardName) => {
    if (this.idFilters.has(card)) {
      this.idFilters.delete(card);
      this.reload();
      return;
    }

    this.idFilters.set(card, this.getCardCaregiverIds(card));
    this.reload();
  };

  private getCardCaregiverIds = (card: CardName): CaregiverId[] => {
    switch (card) {
      case "pendingApplicationCaregivers":
        // Derived from the stats rather than from a local caregiver collection:
        // the employment buckets already carry the ids, per status.
        return (this.stats?.employment ?? [])
          .filter((entry) => entry.status === "PENDING")
          .flatMap((entry) => entry.caregiverIds);

      case "incompliantCaregiversWithActiveCase":
        return this.stats?.incompliantCaregiversWithActiveCase ?? [];

      case "caregiversWithOnlyOneIncompliantItem":
        return this.stats?.caregiversWithOnlyOneIncompliantItem ?? [];

      case "incompliantItemsWithUploadedDocuments":
        return this.stats?.incompliantItemsWithUploadedDocuments.caregiverIds ?? [];

      default:
        assertNever(card);
    }
  };

  public onChartFilter = (chartName: ChartName, labels: ChartLabel[], needsDigest?: boolean) => {
    this.hiddenChartLabels[chartName] = labels
      .filter((label) => label.isHidden)
      .map((label) => label.label);

    if (labels.every((label) => !label.isHidden)) {
      this.idFilters.delete(chartName);
    } else {
      const activeLabels = new Set(labels.filter((label) => !label.isHidden).map((l) => l.label));

      this.idFilters.set(chartName, this.getChartCaregiverIds(chartName, activeLabels));
    }

    this.reload();

    if (needsDigest !== false) {
      this.$scope.$digest();
    }
  };

  private getChartCaregiverIds = (
    chartName: ChartName,
    activeLabels: Set<string>
  ): CaregiverId[] => {
    switch (chartName) {
      case "employmentStatus": {
        const statuses = new Set(
          [...activeLabels].map((label) =>
            label === "Future status of on hold" ? label : label.toUpperCase()
          )
        );

        return (this.stats?.employment ?? [])
          .filter((entry) => statuses.has(entry.status))
          .flatMap((entry) => entry.caregiverIds);
      }

      case "complianceStatus":
        return (this.stats?.caregivers ?? [])
          .filter((entry) => activeLabels.has(entry.status))
          .flatMap((entry) => entry.caregiverIds);

      case "itemsComplianceStatus":
        return (this.stats?.items ?? [])
          .filter(
            (entry) =>
              (this.selectedDocumentTypeIds.size === 0 ||
                this.selectedDocumentTypeIds.has(entry.documentTypeId)) &&
              activeLabels.has(entry.status)
          )
          .flatMap((entry) => entry.caregiverIds);

      default:
        assertNever(chartName);
    }
  };

  public removeFilter = (key: string) => {
    if (this.idFilters.has(key as IdFilterKey)) {
      this.idFilters.delete(key as IdFilterKey);

      if (key in this.charts) {
        this.hiddenChartLabels[key as ChartName] = [];
        // Redrawn from a new array so the chart component picks the change up
        // and un-hides its slices.
        this.charts[key as ChartName] = [...this.charts[key as ChartName]];
      }

      this.reload();
      return;
    }

    switch (key) {
      case "freeText":
        this.filters.freeText = "";
        break;
      case "caregiverStatus":
        this.filters.dropdowns.caregiverStatus.selected = [];
        break;
      case "caregiverComplianceStatus":
        this.filters.dropdowns.complianceStatus.selected = [];
        break;
      case "IsMedflytCG":
        this.filters.dropdowns.medflytCG.selected = [];
        break;
      case "hasHireDate":
        this.filters.dropdowns.hasHireDate.selected = [];
        break;
      case "certifications":
        this.filters.dropdowns.certifications.selected = [];
        break;
    }

    this.reload();
  };

  private refreshFilterChips = () => {
    const chips: FilterChip[] = [];
    const dropdowns = this.filters.dropdowns;
    const search = this.filters.freeText.trim();

    if (search !== "") {
      chips.push({ key: "freeText", title: "Search", values: [search] });
    }
    if (dropdowns.caregiverStatus.selected.length > 0) {
      chips.push({
        key: "caregiverStatus",
        title: "Caregiver status",
        values: dropdowns.caregiverStatus.selected.map((entry) => entry.id),
      });
    }
    if (dropdowns.complianceStatus.selected.length > 0) {
      chips.push({
        key: "caregiverComplianceStatus",
        title: "Caregiver compliance status",
        values: dropdowns.complianceStatus.selected.map((entry) => entry.id),
      });
    }
    if (dropdowns.medflytCG.selected.length > 0) {
      chips.push({
        key: "IsMedflytCG",
        title: "Is Medflyt CG",
        values: dropdowns.medflytCG.selected.map((entry) => entry.id),
      });
    }
    if (dropdowns.hasHireDate.selected.length > 0) {
      chips.push({
        key: "hasHireDate",
        title: "Has hire date",
        values: dropdowns.hasHireDate.selected.map((entry) => entry.id),
      });
    }
    if (dropdowns.certifications.selected.length > 0) {
      chips.push({
        key: "certifications",
        title: "Certifications",
        values: dropdowns.certifications.selected.map((entry) => entry.id),
      });
    }

    // The id filters have no value to show - the title already says what they
    // are ("Are missing only 1 item to be compliant"), and the ids behind them
    // are not something to render.
    for (const key of this.idFilters.keys()) {
      chips.push({ key, title: this.idFilterTitles[key], values: [] });
    }

    this.activeFilters = chips;
  };

  /* ------------------------------------------------------------- documents -- */

  public onDocumentSelect = (records: MultiselectCallbackParameter<CaregiverDocumentTypeId>) => {
    this.selectedDocumentTypeIds = new Set(records.map((record) => record.id));
    this.initItemsComplianceStatusChartEntries();
    this.toggleTableColumns();

    // The per-item chart's slices are scoped to the selected document types, so
    // an id filter taken from it no longer describes what the chart shows.
    if (this.idFilters.delete("itemsComplianceStatus")) {
      this.reload();
    }
  };

  private setDocumentTypes = (types: ComplianceDocument[]) => {
    this.documentTypes = types;
    this.documentTypesMap = Object.fromEntries(types.map((type) => [type.name, type]));

    // Rebuilt on every page rather than only the first: `buildColumns` derives
    // everything it needs from state and re-applies the sort marker, so it is
    // idempotent, and the document types are only known once a page has landed.
    this.buildColumns();
  };

  /** The document types whose columns the current certification filter shows. */
  private relevantDocumentTypes = (): ComplianceDocument[] => {
    const documents = this.documentTypes ?? [];
    const certifications = this.filters.dropdowns.certifications.selected.map((entry) => entry.id);

    if (certifications.length === 0) {
      return documents;
    }

    return documents.filter((type) =>
      type.certifications.some((cert) => certifications.includes(String(cert)))
    );
  };

  private buildColumns = () => {
    const documents = this.relevantDocumentTypes();
    const caregiverColumns: TableColumn[] = ["Caregiver Name", "ID", "Compliance"].map((title) => ({
      title,
      section: "Caregiver Information",
      colored: false,
      sortable: true,
      // Re-applied rather than dropped: a rebuild must not silently clear the
      // arrow on the column the server is actually ordering by.
      sort: SORTABLE_COLUMNS[title] === this.sortField ? this.sortOrder : undefined,
      show: true,
      caregiverDocumentTypeId: null,
    }));

    this.dropdownDocumentOptions = documents.map((document) => ({
      id: document.id,
      label: document.name,
    }));

    const rootDocumentsRecords = Object.fromEntries(
      documents.flatMap((document) => (document.isRoot ? [[document.name, document]] : []))
    );

    this.tableDocumentsColumns = this.colorColumns([
      ...caregiverColumns,
      ...Object.values(rootDocumentsRecords)
        .map((document) => ({
          title: document.name,
          section: parseSectionLabel(document.section),
          colored: false,
          sortable: false,
          show: true,
          caregiverDocumentTypeId: document.id,
        }))
        .sort(this.sortSections),
    ]);

    this.tableDocumentsColumnsGroups = [...groupBy(this.tableDocumentsColumns, "section")].map(
      ([section, columns]) => ({
        section,
        columns,
        count: section === "Caregiver Information" ? columns.length + 1 : columns.length,
        show: true,
      })
    );

    this.toggleTableColumns();
  };

  private toggleTableColumns = () => {
    this.tableDocumentsColumns.forEach((column) => {
      if (column.caregiverDocumentTypeId === null) {
        return;
      }

      column.show =
        this.selectedDocumentTypeIds.size === 0 ||
        this.selectedDocumentTypeIds.has(column.caregiverDocumentTypeId);
    });

    this.tableDocumentsColumnsGroups.forEach((group) => {
      const count = group.columns.filter((column) => column.show).length;
      group.count = group.section === "Caregiver Information" ? count + 1 : count;
      group.show = count > 0;
    });
  };

  private colorColumns = (columns: TableColumn[]): TableColumn[] => {
    let colored = false;

    return columns.map((column, i) => {
      colored = i === 0 || columns[i - 1].section === column.section ? colored : !colored;

      return { ...column, colored };
    });
  };

  private sortSections = (a: TableColumn, b: TableColumn): number => {
    const defaultSections = ["General", "Medical", "I9"];

    if (defaultSections.includes(a.section) && !defaultSections.includes(b.section)) {
      return -1;
    }

    if (!defaultSections.includes(a.section) && defaultSections.includes(b.section)) {
      return 1;
    }

    return a.section.localeCompare(b.section);
  };

  /* ------------------------------------------------------------ cell text -- */

  public getDisplayText = (title: string, row: TableRow) => {
    const item = row.items[title];

    if (item?.display.text !== undefined) {
      return item.display.text;
    }

    return this.isRequiredDocument(row, this.documentTypesMap[title]) ? "Missing" : "Not required";
  };

  public getDisplayTextColor = (title: string, row: TableRow) => {
    const item = row.items[title];

    if (item?.display.color !== undefined) {
      return item.display.color;
    }

    return this.isRequiredDocument(row, this.documentTypesMap[title]) ? "red" : "gray";
  };

  /**
   * A followup document only counts as required when this caregiver is actually
   * missing it - read off the row rather than out of an agency-wide map.
   */
  private isRequiredDocument = (row: TableRow, documentType?: ComplianceDocument) => {
    if (documentType === undefined) {
      return false;
    }

    return (
      documentType.followupParentId === null ||
      row.missingFollowupDocumentTypeIds.has(documentType.id)
    );
  };

  private parseItemDisplayData = (
    item: DashboardComplianceItem
  ): { text: string; color: LabelColor } => {
    switch (item.status) {
      case "Not required":
        return { text: "Not required", color: "gray" };

      case "Not due yet":
        return {
          text:
            item.dueDate === null || item.dueDate.compareTo(LocalDate.parse("3000-01-01")) === 0
              ? "Not due yet"
              : "Due in " + dateUtils.localDateToMDYString(item.dueDate),
          color:
            (item.dueDate?.compareTo(LocalDate.now().plusDays(30)) ?? 0) > 0 ? "gray" : "orange",
        };

      case "Resolved":
        return { text: "Resolved", color: "gray" };

      case "Missing":
        return { text: "Missing", color: this.getItemColorLabel(item) };

      case "Pending Uploads":
        return { text: "Pending Uploads", color: this.getItemColorLabel(item) };

      case "Compliant":
      case "Not Compliant":
        return { text: this.getDueDateText(item), color: this.getItemColorLabel(item) };

      default:
        assertNever(item.status);
    }
  };

  private getItemColorLabel = (item: DashboardComplianceItem): LabelColor => {
    if (item.status === "Pending Uploads") {
      return "orange";
    }

    if (item.status === "Compliant") {
      return (item.expiryDate?.compareTo(LocalDate.now().plusDays(30)) ?? 0) < 0
        ? "orange"
        : "green";
    }

    return "red";
  };

  private getDueDateText = (item: DashboardComplianceItem) => {
    return [item.effectiveDate, item.expiryDate]
      .flatMap((date) =>
        date === null || date === undefined ? [] : [dateUtils.localDateToMDYString(date)]
      )
      .join(" - ");
  };

  /* ---------------------------------------------------------------- stats -- */

  public getStats = () => {
    this.compService
      .fetchStats()
      .then((data) => {
        this.stats = data;
        this.setCharts();
      })
      .catch((err) => {
        console.error(err);
        this.toaster.error("Could not load compliance stats");
      })
      .finally(() => {
        this.isLoadingStats = false;
      });
  };

  public pendingApplicationCount = (): number =>
    (this.stats?.employment ?? [])
      .filter((entry) => entry.status === "PENDING")
      .reduce((total, entry) => total + entry.caregiverIds.length, 0);

  private setCharts = () => {
    if (this.stats === undefined) {
      return;
    }

    this.charts.employmentStatus = this.stats.employment.map(({ status, caregiverIds }) => ({
      label: upperCaseFirst(status),
      value: caregiverIds.length,
      color:
        status === "Future status of on hold"
          ? PieChartColor.yellow
          : this.parseColor(getCaregiverStatusColor(status)),
    }));

    this.charts.complianceStatus = this.stats.caregivers.map(({ status, caregiverIds }) => ({
      label: upperCaseFirst(status),
      value: caregiverIds.length,
      color: getCaregiverOverviewComplianceStatusColor(status),
    }));

    this.initItemsComplianceStatusChartEntries();
  };

  private initItemsComplianceStatusChartEntries = () => {
    const items = sumItemsStatusGroups(
      groupBy(
        (this.stats?.items ?? [])
          .filter(
            (item) =>
              this.selectedDocumentTypeIds.size === 0 ||
              this.selectedDocumentTypeIds.has(item.documentTypeId)
          )
          .map(({ documentTypeId, status, caregiverIds }) => ({
            documentTypeId,
            status,
            totalCount: caregiverIds.length,
          })),
        "status"
      )
    );

    this.charts.itemsComplianceStatus = Array.from(items).map(([status, totalCount]) => ({
      label: upperCaseFirst(status),
      value: totalCount,
      color: getComplianceItemOverviewStatusColor(status),
    }));
  };

  private parseColor = (color: LabelColor): PieChartColor => {
    switch (color) {
      case "red":
        return PieChartColor.red;
      case "green":
        return PieChartColor.green;
      case "orange":
        return PieChartColor.orange;
      case "gray":
        return PieChartColor.gray;
      case "lightblue":
        return PieChartColor.blue;
    }
  };

  /* ------------------------------------------------------------ selection -- */

  public onRowClick = (row: TableRow) => {
    row.isChecked = !row.isChecked;
    this.onCheckRow(row);
  };

  public onCheckRow = (row: TableRow) => {
    if (row.isChecked) {
      this.selectedItems.add(row);
    } else {
      this.selectedItems.delete(row);
    }
  };

  /**
   * Selects the loaded page, and only it.
   *
   * Deliberately a plain checkbox rather than the shared `select-all-checkbox`:
   * that component offers "all" and "this page" as separate choices, and here
   * "all" would silently mean the same thing as "this page" while reading as the
   * whole result set.
   */
  public isPageFullyChecked = (): boolean =>
    this.rows.length > 0 && this.rows.every((row) => row.isChecked);

  public toggleAllOnPage = () => {
    const check = !this.isPageFullyChecked();

    this.rows.forEach((row) => {
      row.isChecked = check;
    });

    this.selectedItems = check ? new Set(this.rows) : new Set();
  };

  public openCaregiverModal = (id: CaregiverId) => this.$rootScope.openCaregiverModal(id);

  /**
   * Sends reminders to the caregivers selected on the loaded page.
   *
   * The old dashboard could select across the whole agency because the whole
   * agency was in the browser. Selection is per page here, and the modal says so
   * by listing exactly who it will message.
   */
  public onClickSendReminders = () => {
    const caregivers = Array.from(this.selectedItems).map((row) => row.caregiver);

    const modal = this.mfModal.create({
      subject: "Send compliance reminders",
      message:
        "Using this feature will send reminders to the selected caregivers through medflyt's app chat.",
      cancelLabel: "Cancel",
      confirmLabel: "Send reminders",
      showInput: true,
      inputPlaceholder:
        "Write something like: Hi, your flu shot is about to expire. Please submit a new one.",
      inputIsRequired: true,
      inputType: "textarea",
      layoutOrder: ["message", "customTemplate", "input"],
      hideCancelButton: false,
      preventBackdropClose: true,
      customTemplate: "reminders-modal-content",
      data: {
        selectedCaregivers: caregivers,
      },
      onConfirm: ({ inputModel }: { inputModel: string }) => {
        const confirmationModal = this.mfModal.create({
          subject: `${caregivers.length} Caregivers will receive the following message:`,
          message: inputModel,
          cancelLabel: "Cancel",
          confirmLabel: "Confirm",
          preventBackdropClose: true,
          onConfirm: () => {
            confirmationModal.setLoading(true);
            this.compService
              .sendReminders(
                caregivers.map((caregiver) => caregiver.id),
                inputModel
              )
              .then(() => {
                this.toaster.success("Reminders sent successfully");
                modal.close();
              })
              .catch(() => {
                this.toaster.error("Failed to send reminders");
              })
              .finally(() => {
                confirmationModal.close();
              });
          },
          onComplete: () => {
            confirmationModal.close();
          },
        });
      },
    });
  };
}

interface Component extends angular.IComponentOptions {
  $name: string;
}

export const complianceDashboardPagedComponent: Component = {
  $name: "complianceDashboardPaged",
  templateUrl:
    "admin/modules/compliance/components/compliance-dashboard-paged/compliance-dashboard-paged.component.html",
  controller: ComplianceDashboardPagedCtrl,
  controllerAs: "ctrl",
};
